/* eslint-disable max-len */
import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { TireItem } from '@1po/1po-bff-fe-spec/generated/tire/model/TireItem';
import { ROUTER_CART, ROUTER_ESTIMATE } from 'app/AppRouter';
import { RootState } from 'app/AppStore';
import { trackAppEvent } from 'app/AppTracker';
import { DiscountCorner } from 'components/Discount';
import { getTechnicalCharacteristicsLabel } from 'components/StockInfo/catalogStockUtils';
import {
  addTireFromCatalogToCart,
  getLastSearchedVehicleKey,
  getSearchVehicleResult,
} from 'domains/catalog/Catalog.store';
import { LIFECYCLE_W1, RENATIVE_LABEL, RENATIVE_TECHNICAL_CHARACTERISTICS_CODE } from 'domains/catalog/Catalog.types';
import { useIsCatalog } from 'domains/catalog/Catalog.utils';
import { convertTireToEstimateTire } from 'domains/estimate/Estimate.mapper';
import { addCatalogTire } from 'domains/estimate/Estimate.store';
import { getDHReference, getIsMprAccepted, getIsStockAvailable, getPrice, getStockInfo } from 'domains/references';
import { convertTireItemToTireReference } from 'domains/tires/Tire.mapper';
import { useFetchMprValidation, useFetchTireReplacementProducts } from 'domains/tires/Tire.requests';
import { getTireSearchResult, getTireSetParam } from 'domains/tires/Tire.store';
import { GarageView, getUserCountry, SparePartsViewType } from 'domains/user';
import { useMyStoreTireDiscount } from 'pages/MyStorePage/useMyStoreTireDiscount';
import { AvailabilityWrapper } from 'pages/TiresPage/TireReferencesSection/TireReferenceCard/Components/AvailabilityWrapper';
import { BrandBlock } from 'pages/TiresPage/TireReferencesSection/TireReferenceCard/Components/BrandBlock';
import { CompactStockBadge } from 'pages/TiresPage/TireReferencesSection/TireReferenceCard/Components/CompactStockBadge';
import { FeaturesBlock } from 'pages/TiresPage/TireReferencesSection/TireReferenceCard/Components/FeaturesBlock';
import { NoLongerAvailableInfo } from 'pages/TiresPage/TireReferencesSection/TireReferenceCard/Components/NoLongerAvailableInfo';
import { PriceActionsBlock } from 'pages/TiresPage/TireReferencesSection/TireReferenceCard/Components/PriceActionsBlock';
import { RatingsBlock } from 'pages/TiresPage/TireReferencesSection/TireReferenceCard/Components/RatingsBlock';
import { RefInfoRow } from 'pages/TiresPage/TireReferencesSection/TireReferenceCard/Components/RefInfoRow';
import { ReplacementProductsPanel } from 'pages/TiresPage/TireReferencesSection/TireReferenceCard/Components/ReplacementProductsPanel';
import { SelectionBadge } from 'pages/TiresPage/TireReferencesSection/TireReferenceCard/Components/SelectionBadge';
import { StockDetailSection } from 'pages/TiresPage/TireReferencesSection/TireReferenceCard/Components/StockDetailSection';
import { TitleBlock } from 'pages/TiresPage/TireReferencesSection/TireReferenceCard/Components/TitleBlock';
import { UnavailableBox } from 'pages/TiresPage/TireReferencesSection/TireReferenceCard/Components/UnavailableBox';
import { SCard } from 'pages/TiresPage/TireReferencesSection/TireReferenceCard/TireReferenceCard.styled';
import { tireBrandsData } from 'pages/TiresPage/TireSearch/TiresSearchbar/TireBrandsData';
import { Flex, MarginBox, NotificationLink, notifyTop } from 'UI';
import { getData, useExtraLarge } from 'utils';
import {
  TRACKING_EVENT_ADD_TIRES_TO_CART,
  TRACKING_EVENT_ADD_TIRES_TO_ESTIMATE,
  TRACKING_EVENT_GO_TO_CART_SHORTCUT,
  TRACKING_EVENT_GO_TO_ESTIMATE,
} from 'utils/eventTracker/EventTracker.types';

interface TireReferenceCardProps {
  tire: TireItem;
  sparePartsView: SparePartsViewType;
  compact?: boolean;
}

const TireReferenceCard = React.memo(function TireReferenceCard({
  tire,
  sparePartsView,
  compact,
}: TireReferenceCardProps) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();
  const availableInUserCountry = useSelector((state: RootState) => getIsStockAvailable(state, tire.partNumber));
  const extraLarge = useExtraLarge();
  const price = getData(useSelector((state: RootState) => getPrice(state, tire.partNumber)));
  const tireSetParam = useSelector(getTireSetParam);
  const tireBrand = tireBrandsData.find((brand) => tire.brandId === brand.brandId);
  const myStoreTireDiscount = useMyStoreTireDiscount(tire.brandName, tire.baseDiameter);
  const isCatalog = useIsCatalog();
  const stock = useSelector((state: RootState) => getStockInfo(state, tire.partNumber));
  const userCountry = useSelector(getUserCountry);
  const hasStockInR1 = stock?.data?.warehouses.some((w) => w.type === 'LOCAL' && w.confirmedQuantity > 0) ?? false;
  const vehicleKey = useSelector(getLastSearchedVehicleKey);
  const vehicle = useSelector((state: RootState) => getSearchVehicleResult(state, vehicleKey));
  const vehicleDetail = vehicle?.vehicleDetail;
  const tireReference = convertTireItemToTireReference(tire);
  const tireRef = useSelector((state: RootState) =>
    getDHReference(state, { referenceNumber: tire.partNumber, vehicleKey }),
  );
  const renative =
    getTechnicalCharacteristicsLabel(getData(tireRef), RENATIVE_TECHNICAL_CHARACTERISTICS_CODE) === RENATIVE_LABEL;

  useFetchMprValidation(tire.partNumber);

  const accepted =
    useSelector((state: RootState) => getIsMprAccepted(state, tire.partNumber) ?? true) ||
    (userCountry === 'FR' && hasStockInR1);
  const isAvailableSoon = tire.lifeCycle === LIFECYCLE_W1;
  const isNoLongerAvailable = tire.isNonCommercialized || !accepted;

  useFetchTireReplacementProducts(tire.partNumber, compact);

  const replacementTires = useSelector((state: RootState) =>
    getTireSearchResult(state, {
      searchParamsBase64: tire.partNumber,
    }),
  );
  const replacementTiresFound = replacementTires?.data?.tires;

  const handleAddToCartClick = () => {
    notifyTop(
      'success',
      <Trans i18nKey={'catalog.tires.reference_card.added_to_basket.description'}>
        {'Tire has been added to your cart'}
      </Trans>,
      undefined,
      <NotificationLink
        onClick={() => {
          trackAppEvent(TRACKING_EVENT_GO_TO_CART_SHORTCUT);
          history.push(ROUTER_CART);
        }}
      >
        <Trans i18nKey={'catalog.reference_card.added_to_basket.go_to_cart'}>{'Go to cart'}</Trans>
      </NotificationLink>,
    );
    dispatch(addTireFromCatalogToCart({ tire }));
    trackAppEvent(TRACKING_EVENT_ADD_TIRES_TO_CART);
  };

  const handleAddToEstimateClick = (quantity?: number) => {
    if (price?.garageView?.vatExcludedPrice) {
      notifyTop(
        'success',
        <Trans count={quantity ?? 1} i18nKey={'catalog.reference_card.added_to_estimate.description.v2'}>
          {'{{ count }} Reference has been added to your estimate.'}
        </Trans>,
        undefined,
        <NotificationLink
          onClick={() => {
            trackAppEvent(TRACKING_EVENT_GO_TO_ESTIMATE);
            history.push(`${ROUTER_ESTIMATE}`);
          }}
        >
          <Trans i18nKey={'catalog.parts.category.car_parts.labor_time.notification.see_estimate'}>
            {'See estimate'}
          </Trans>
        </NotificationLink>,
      );
      trackAppEvent(TRACKING_EVENT_ADD_TIRES_TO_ESTIMATE);
      dispatch(
        addCatalogTire({
          reference: convertTireToEstimateTire(
            tire.partNumber,
            tire.profile ?? '',
            price,
            true,
            quantity ?? tireSetParam ?? 1,
            String(tire.baseDiameter),
            tire.brandId,
            tire.eprelUrl,
            tire.designation,
          ),
        }),
      );
    }
  };

  const dim = (isAvailableSoon || isNoLongerAvailable) && !compact;
  return (
    <>
      <SelectionBadge visible={!compact} />
      <SCard>
        {sparePartsView === GarageView && (
          <DiscountCorner reference={tire.partNumber} type={compact ? 'catalog_mini' : 'catalog'} />
        )}
        <Flex direction={'row'} basis={5}>
          <BrandBlock
            compact={compact}
            availableInUserCountry={availableInUserCountry}
            referenceBrandId={tire.brandId}
            renative={renative}
          />
          <Flex size={compact ? 1 : 3} align="center">
            <AvailabilityWrapper dim={dim}>
              <MarginBox mr={10}>
                <Flex direction="column" minWidth={extraLarge ? 315 : 0} wrap="wrap">
                  <TitleBlock
                    compact={compact}
                    availableInUserCountry={availableInUserCountry}
                    extraLarge={extraLarge}
                    partNumber={String(tire.partNumber)}
                    profile={tire.profile}
                    designation={tire.designation}
                    brandLabel={tireBrand?.name}
                  />
                  {compact && (
                    <RefInfoRow
                      partNumber={String(tire.partNumber)}
                      brandName={tire.brandName}
                      brandLabel={tireBrand?.name}
                      tireReference={tireReference}
                      vehicleDetail={vehicleDetail}
                    />
                  )}
                  {!compact && (
                    <>
                      <FeaturesBlock
                        partNumber={String(tire.partNumber)}
                        season={tire.season}
                        extraLoad={tire.extraLoad}
                        runFlat={tire.runFlat}
                        pmsf={tire.pmsf}
                        selfSeal={tire.selfSeal}
                        lifeCycle={tire.lifeCycle}
                        rimsProtection={tire.rimsProtection}
                        marquage={tire.marquage}
                        renative={renative}
                      />
                      <RatingsBlock
                        availableInUserCountry={availableInUserCountry}
                        wetGrip={tire.wetGrip}
                        exteriorSound={tire.exteriorSound}
                        fuelConsumption={tire.fuelConsumption}
                      />
                      <RefInfoRow
                        partNumber={String(tire.partNumber)}
                        brandName={tire.brandName}
                        brandLabel={tireBrand?.name}
                        tireReference={tireReference}
                        vehicleDetail={vehicleDetail}
                      />
                    </>
                  )}
                </Flex>
              </MarginBox>
            </AvailabilityWrapper>
          </Flex>
          {compact && (
            <Flex direction={'row'} align={'center'}>
              <FeaturesBlock
                partNumber={String(tire.partNumber)}
                season={tire.season}
                extraLoad={tire.extraLoad}
                runFlat={tire.runFlat}
                pmsf={tire.pmsf}
                selfSeal={tire.selfSeal}
                lifeCycle={tire.lifeCycle}
                rimsProtection={tire.rimsProtection}
                marquage={tire.marquage}
                renative={renative}
              />
              <RatingsBlock
                availableInUserCountry={availableInUserCountry}
                wetGrip={tire.wetGrip}
                exteriorSound={tire.exteriorSound}
                fuelConsumption={tire.fuelConsumption}
              />
            </Flex>
          )}
          {availableInUserCountry ? (
            <Flex direction="row" justify="flex-end" align={compact ? 'center' : 'flex-start'}>
              {compact && <CompactStockBadge referenceNumber={String(tire.partNumber)} />}
              <PriceActionsBlock
                compact={compact}
                isCatalog={isCatalog}
                isAvailableSoon={isAvailableSoon}
                isNoLongerAvailable={isNoLongerAvailable}
                price={price}
                partNumber={String(tire.partNumber)}
                sparePartsView={sparePartsView}
                clientDiscount={myStoreTireDiscount?.discount}
                tireSetParam={tireSetParam}
                onAddToCart={handleAddToCartClick}
                onAddToEstimate={handleAddToEstimateClick}
                dim={dim}
              />
            </Flex>
          ) : (
            <UnavailableBox
              message={t(
                'catalog.tire.reference.unavailable',
                'Looks like this item is not available in your country.',
              )}
              extraLarge={extraLarge}
            />
          )}
        </Flex>
        {!compact && availableInUserCountry && isNoLongerAvailable && (
          <NoLongerAvailableInfo
            header={t('catalog.tires.search.no_longer_available_header', 'Product no longer available')}
            text={t(
              'catalog.tires.search.no_longer_available_text',
              'We have listed our best equivalent products below',
            )}
          />
        )}
      </SCard>
      {!compact && (
        <StockDetailSection
          visible={!compact && !(isAvailableSoon || isNoLongerAvailable)}
          lifeCycle={tire.lifeCycle}
          partNumber={String(tire.partNumber)}
        />
      )}
      <MarginBox mt={5} />
      <ReplacementProductsPanel
        visible={(isAvailableSoon || isNoLongerAvailable) && !compact}
        replacementTiresFound={replacementTiresFound}
        searchStatus={replacementTires?.searchStatus}
        sparePartsView={sparePartsView}
        isAvailableSoon={isAvailableSoon}
        isNoLongerAvailable={isNoLongerAvailable}
      />
      <MarginBox mt={20} />
    </>
  );
});

export default TireReferenceCard;
